<template>

    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header
            title="Tenant manager"
            info="Create new installations for clients"
            icon="mdi-office-building">
            <template slot="buttons">

                <v-tooltip color="primary" top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                               v-on="on"
                               color="primary"
                               fab
                               small
                               class="elavation-0 mr-2"
                               @click="addNewTenantClicked">
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </template>
                    <span>Create new tenant</span>
                </v-tooltip>

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn
                               @click="openInfoDialog(currentTenant)"
                               v-on="on"
                               icon>
                            <v-icon>mdi-information</v-icon>
                        </v-btn>
                    </template>
                    <span>Show info about the current tenant</span>
                </v-tooltip>

                <v-tooltip top color="primary">
                    <template v-slot:activator="{ on }">
                        <v-btn
                               @click="getData(true)"
                               v-on="on"
                               icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>

            </template>

        </material-header>

        <v-alert v-if="!hasRole('Super Admin')"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions for this feature
        </v-alert>

        <v-card v-if="hasRole('Super Admin')">

            <v-row>

                <v-col cols="12" offset-lg="8" offset-md="6" md="6" lg="4">

                    <v-card flat>
                        <v-card-text class="pt-0">
                            <v-text-field
                                append-icon="mdi-magnify"
                                label="Search"
                                single-line
                                hide-details
                                v-model="search"
                            />
                        </v-card-text>
                    </v-card>

                </v-col>

                <v-col cols="12">

                    <v-data-table dark
                                  :key="tenants.length"
                                  :headers="headers"
                                  :search="search"
                                  :items="tenants"
                                  :loading="tenantsLoading"
                                  :options.sync="defaultSort"
                                  :footer-props="{itemsPerPageOptions: [10,25,100, -1]}"
                                  no-results-text="No tenants found!"
                                  no-data-text="No tenants found!"
                    >
                        <template v-slot:body="{ items }">
                            <tbody>
                            <tr v-for="item in items" :key="item.uuid">

                                <!-- PANEL NAME -->
                                <td class="no-wrap">
                                    <v-tooltip top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <v-btn
                                                   @click="openInfoDialog(item)"
                                                   v-on="on"
                                                   icon>
                                                <v-icon>mdi-information</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Show info about this tenant</span>
                                    </v-tooltip>

                                    {{ item.panel_name }}
                                </td>

                                <!-- PANEL URL -->
                                <td>
                                    <v-tooltip top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <v-chip v-on="on"
                                                    @click="editDomain(item)">
                                                {{ item.fqdn }}
                                            </v-chip>
                                        </template>
                                        <span>Update client domain</span>
                                        <div>UUID: {{ item.uuid }}</div>
                                    </v-tooltip>
                                </td>

                                <!-- CONTACT NAME -->
                                <td class="no-wrap">
                                    <v-tooltip color="primary" top>
                                        <template v-slot:activator="{ on }">
                                            <div v-on="on">
                                                {{ item.contact_name }}
                                            </div>
                                        </template>
                                        <span>{{ item.contact_email }}</span>
                                    </v-tooltip>
                                </td>

                                <!-- CREATED AT -->
                                <td class="no-wrap">
                                    <v-tooltip top color="primary">
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on"> {{ toHumanFrom(item.created_at) }} </span>
                                        </template>
                                        <span>
                                            {{ toLocalDateTime(item.created_at) }}
                                        </span>
                                    </v-tooltip>
                                </td>

                                <!-- DEMO MODE -->
                                <td class="text-left">
                                    <v-btn color="primary" small @click="featuresClicked(item)">FEATURES</v-btn>
                                </td>

                                <!-- DEMO MODE -->
                                <td class="text-left">
                                    <v-chip color="primary" v-show="item.demo_mode">SIMULATION</v-chip>
                                </td>

                                <!-- TRIAL OR PAYING CUSTOMER -->
                                <td>
                                    <v-tooltip top color="primary" v-if="item.trial_expires_on">
                                        <template v-slot:activator="{ on }">
                                            <v-chip v-on="on"
                                                    pill
                                                    @click="openTrialDateSelector(item)"
                                                    :color="isExpired(item) ? 'error' : 'warning'"
                                                    text-color="white">
                                                <v-avatar left>
                                                    <v-icon>mdi-update</v-icon>
                                                </v-avatar>
                                                {{ toHumanTill(item.trial_expires_on) }}
                                            </v-chip>
                                        </template>
                                        <span>{{ toLocalDate(item.trial_expires_on) }}</span>
                                    </v-tooltip>

                                    <v-chip v-if="!item.trial_expires_on"
                                            @click="openTrialDateSelector(item)"
                                            color="success"
                                            pill
                                            text-color="white">
                                        <v-avatar>
                                            <v-icon>mdi-check-circle</v-icon>
                                        </v-avatar>
                                        Paying customer
                                    </v-chip>
                                </td>

                                <td class="text-right pl-0">

                                    <div style="white-space: nowrap;">

                                        <v-tooltip top color="primary">
                                            <template v-slot:activator="{ on }">
                                                <v-btn class="ma-0"
                                                       icon
                                                       :disabled="!canBeDeleted(item)"
                                                       color="primary"
                                                       v-on="on"
                                                       @click.stop="deleteTenantClicked(item)">

                                                    <v-icon light small color="white">mdi-delete-sweep
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span v-if="!canBeDeleted(item)">You can only delete expired trials</span>
                                            <span v-if="canBeDeleted(item)">Delete tenant and all data</span>
                                        </v-tooltip>

                                        <v-tooltip top color="primary">
                                            <template v-slot:activator="{ on }">
                                                <v-btn class="ma-0"
                                                       icon
                                                       color="primary"
                                                       v-on="on"
                                                       @click.stop="openTenantUrl(item.fqdn)">

                                                    <v-icon light small color="white">mdi-send</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Goto client domain</span>
                                        </v-tooltip>
                                    </div>
                                </td>

                            </tr>
                            </tbody>

                        </template>

                    </v-data-table>
                </v-col>

            </v-row>
        </v-card>

        <tenant-info-dialog
            :tenant="tenantUnderEdit"
            v-if="currentTenant"
            v-model="infoDialogModel"
        />

        <tenant-form-dialog
            v-model="formDialogModel"
            :key="formKey"
            :locales="locales"
            :saving="tenantSaving"
            :tenant="tenantUnderEdit"
            @change="tenantSubmitted"
        />

        <trial-date-dialog
            v-model="trialDateDialogModel"
            v-if="tenantUnderEdit"
            :date="tenantUnderEdit.trial_expires_at"
            :tenant="tenantUnderEdit"
            @change="trialDateChanged"
        />

        <confirmation-dialog
            v-model="confirmationDialogModel"
            :tenant="tenantUnderEdit"
            @change="deleteConfirmed"
        />

        <features-dialog
            v-model="featuresDialogModel"
            :key="tenantUnderEdit.uuid"
            :tenant="tenantUnderEdit"
        />

    </v-container>
</template>

<script>

import Store from '../store';
import {mapState, mapGetters, mapActions} from 'vuex';
import TenantInfoDialog from './TenantInfoDialog.vue';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import ToolbarPage from '../../../components/UI/ToolbarPage';
import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
import TenantFormDialog from './TenantFormDialog';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import TrialDateDialog from './TrialDateDialog';
import ConfirmationDialog from './TenantDeleteConfirmationDialog';
import moment from 'moment';
import FeaturesDialog from "./FeaturesDialog";
import Json from 'vue-json-pretty';

const vuexModule = 'TenantManager';

export default {

    mixins: [activeSessionChecker, timeDisplay],

    components: {
        FeaturesDialog,
        TrialDateDialog,
        ToolbarPage,
        TenantInfoDialog,
        TenantFormDialog,
        MaterialHeader,
        ConfirmationDialog,
        Json
    },

    data: function () {

        return {
            infoDialogModel: false,
            formDialogModel: false,
            trialDateDialogModel: false,
            tenantUnderEdit: {},

            defaultSort: {
                sortBy: ['panel_name'],
                sortDesc: [false],
            },
            search: '',

            headers: [
                {text: 'PANEL NAME', sortable: true, value: 'panel_name', align: 'left'},
                {text: 'URL', sortable: true, value: 'fqdn', align: 'left'},
                {text: 'ADMIN CONTACT', sortable: true, value: 'contact_name', align: 'left'},
                {text: 'CREATED', sortable: true, value: 'created_at', align: 'left'},
                {text: 'FEATURES', sortable: false, align: 'left', width: '65px'},
                {text: 'SIMULATION', sortable: true, value: 'demo_mode', align: 'left', width: '100px'},
                {
                    text: 'TRIAL EXPIRATION',
                    sortable: true,
                    value: 'trial_expires_at',
                    align: 'left',
                    width: '200px'
                },
                {text: 'ACTIONS', sortable: false, value: '', align: 'left', width: '105px'},
            ],

            confirmationDialogModel: false,
            confirmationName: '',
            formKey: 1,
            featuresDialogModel: false,
        }
    },

    mounted() {
        if (this.hasRole('Super Admin')) {

            this.getData();

            this.$store.dispatch('Locales/getLocales')
                .catch(error => {
                    console.error(error);
                });
        }
    },

    computed: {

        ...mapState('Locales', [
            'locales',
        ]),

        ...mapState(vuexModule, [
            'tenants',
            'tenantsLoading',
            'tenantSaving',
        ]),

        ...mapGetters(vuexModule, [
            'currentTenant',
        ]),
    },

    methods: {
        ...mapState('Locales', [
            'getLocales',
        ]),
        ...mapActions(vuexModule, [
            'getData',
            'getCurrent',
            'createTenant',
            'updateTenant',
            'deleteTenant',
            'getFeatures'
        ]),

        openInfoDialog(tenant) {
            this.tenantUnderEdit = Object.assign({}, tenant);
            this.infoDialogModel = true
        },

        tenantSubmitted(data) {

            if (data.uuid) {

                this.updateTenant(data)
                    .then(() => {
                        this.formDialogModel = false;
                    })
                    .catch(response => {
                        Bus.$emit('flash-message', {type: 'error', message: response.data.message});
                    })

            } else {

                this.createTenant(data)
                    .then((response) => {
                        this.formDialogModel = false;
                    })
                    .catch(response => {
                        Bus.$emit('flash-message', {type: 'error', message: response.data.message});
                    })
            }
        },

        openTenantUrl(fqdn) {
            window.open('https://' + fqdn, '_blank').focus();
        },

        openTrialDateSelector(tenant) {
            this.tenantUnderEdit = Object.assign({}, tenant);
            this.trialDateDialogModel = true;
        },

        trialDateChanged(tenant) {

            this.updateTenant(tenant)
                .then(() => {
                    if (tenant.trial_expires_on === null) {
                        const audio = new Audio('/sounds/cashregister.mp3');
                        audio.volume = 1;
                        audio.play();
                        Bus.$emit('flash-message', {
                            type: 'success',
                            message: "Sweet! Paying customer detected :-)"
                        });
                    } else {
                        Bus.$emit('flash-message', {message: "Trial date set"});
                    }
                })
                .catch(response => {
                    Bus.$emit('flash-message',
                        {type: 'error', message: 'Trial date failed to update.'});
                });

            this.trialDateDialogModel = false;
        },

        addNewTenantClicked() {
            this.formKey++;

            this.tenantUnderEdit = {
                uuid: null,
                panel_name: null,
                contact_name: null,
                contact_email: null,
                fqdn: null,
                trial_expires_on: null,
                demo_mode: true,
                default_locale: 'ENG-US'
            };
            this.formDialogModel = true;
        },

        editDomain(tenant) {
            this.tenantUnderEdit = Object.assign({}, this.tenantUnderEdit, tenant);
            this.formDialogModel = true;
        },

        deleteTenantClicked(tenant) {
            this.tenantUnderEdit = Object.assign({}, tenant);
            this.confirmationDialogModel = true;
        },

        deleteConfirmed(tenant) {

            this.confirmationDialogModel = false;

            this.deleteTenant(tenant)
                .then(() => {
                    Bus.$emit('flash-message', {message: "Deleted!"});
                })
                .catch(response => {
                    Bus.$emit('flash-message',
                        {type: 'error', message: response.data.message});
                });
        },

        isExpired(tenant) {
            return moment().isAfter(tenant.trial_expires_on);
        },

        canBeDeleted(tenant) {
            if (Spark.settings.environment === "local" ||
                Spark.settings.environment === "staging") {
                return true;
            } else {
                return this.isExpired(tenant);
            }
        },

        featuresClicked(tenant) {
            this.tenantUnderEdit = tenant;
            this.featuresDialogModel = true;
        },
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    }
}
</script>
